import cn from 'clsx'
import { FC, MouseEvent, ReactNode, useRef } from 'react'

import { IFile } from '@/types/task.interface'
import { toastError } from '@/utils/toastError'

interface IButtonLinkProps {
	type: string
	label: ReactNode
	files: IFile[]
	resolveFile?: (type: string) => Promise<IFile | undefined>
	linkClassName?: string
	textClassName?: string
	ariaLabel?: string
}

const ButtonLink: FC<IButtonLinkProps> = ({
	type,
	label,
	files,
	resolveFile,
	linkClassName,
	textClassName,
	ariaLabel
}) => {
	const skipResolveRef = useRef(false)

	const currentFile = (type: string, files: IFile[]) => {
		return files.find(item => item.name === type)
	}

	const file = currentFile(type, files)

	const handleClick = async (event: MouseEvent<HTMLAnchorElement>) => {
		if (skipResolveRef.current || !resolveFile) {
			skipResolveRef.current = false
			return
		}

		event.preventDefault()

		const anchor = event.currentTarget

		try {
			const resolvedFile = await resolveFile(type)

			if (!resolvedFile) return

			const response = await fetch(resolvedFile.source, {
				headers: {
					Range: 'bytes=0-0'
				}
			})

			if (!response.ok) {
				const data = await response.json().catch(() => null)

				toastError(
					data?.error || `Failed to download file. Error ${response.status}`
				)
				return
			}

			await response.body?.cancel()

			anchor.href = resolvedFile.source
			anchor.download = resolvedFile.filename

			skipResolveRef.current = true
			anchor.click()
		} catch (error) {
			console.error('Failed to prepare file download:', error)
			toastError('Failed to download file. Please try again.')
		}
	}

	return (
		<a
			className={cn(
				'rounded-[32px] z-10 p-2.5 w-fit transition-opacity duration-200 flex items-center justify-center text-white bg-gray-600 hover:bg-gray-500 cursor-pointer',
				linkClassName
			)}
			href={file?.source}
			download={file?.filename}
			aria-label={ariaLabel}
			onClick={handleClick}
		>
			<span className={cn(textClassName)}>{label}</span>
		</a>
	)
}

export default ButtonLink
